import React, { useEffect, useContext, useState, useCallback } from "react";
import { AuthContext } from "./AuthProvider";
import { Layout } from "./Layout";
import { supabase } from "./supabaseClient";
import { FieldValues, useForm } from "react-hook-form";
import { handleError } from "./utils/handleError";
import { ScheduleEntry } from "./types/Profile";
import toast from "react-hot-toast";
import { GenerateMissingImages } from "./components/GenerateMissingImages";

// Common timezones for user selection
const COMMON_TIMEZONES = [
  { value: "UTC", label: "UTC (Coordinated Universal Time)" },
  { value: "America/New_York", label: "Eastern Time (New York)" },
  { value: "America/Chicago", label: "Central Time (Chicago)" },
  { value: "America/Denver", label: "Mountain Time (Denver)" },
  { value: "America/Los_Angeles", label: "Pacific Time (Los Angeles)" },
  { value: "Europe/London", label: "GMT (London)" },
  { value: "Europe/Paris", label: "CET (Paris)" },
  { value: "Europe/Berlin", label: "CET (Berlin)" },
  { value: "Asia/Tokyo", label: "JST (Tokyo)" },
  { value: "Asia/Shanghai", label: "CST (Shanghai)" },
  { value: "Asia/Kolkata", label: "IST (India)" },
  { value: "Australia/Sydney", label: "AEDT (Sydney)" },
];

const Account = () => {
  const [updating, setUpdating] = useState("");
  const [scheduleEntries, setScheduleEntries] = useState<ScheduleEntry[]>([]);
  const [newScheduleEntry, setNewScheduleEntry] = useState<ScheduleEntry>({
    day: "",
    time: "",
  });
  const [postTemplate, setPostTemplate] = useState("");
  const { session } = useContext(AuthContext);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const getProfile = useCallback(async () => {
    try {
      let { data, error, status } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session?.user?.id!)
        .single();

      if (error && status !== 406) {
        throw error;
      }

      if (data) {
        reset(data);
        // Load schedule from profile data or fallback to localStorage
        const profileData = data as any;
        if (profileData.posting_schedule) {
          setScheduleEntries(profileData.posting_schedule);
        } else {
          // Fallback to localStorage for existing users
          const savedSchedule = localStorage.getItem(
            `schedule_${session?.user?.id}`
          );
          if (savedSchedule) {
            setScheduleEntries(JSON.parse(savedSchedule));
          }
        }
        // Load post template
        if (profileData.post_template) {
          setPostTemplate(profileData.post_template);
        }
      }
    } catch (error) {
      handleError(error as Error);
    }
  }, [session?.user?.id, reset]);

  useEffect(() => {
    getProfile();
  }, [getProfile]);

  const updateProfile = async (content: FieldValues) => {
    setUpdating("profile");

    try {
      const updates = {
        id: session?.user.id,
        ...content,
      };

      if ("facebook_page_search" in updates) {
        delete updates.facebook_page_search; // Remove if not needed
      }

      console.log("Updating profile with content:", content);

      let { error } = await supabase.from("profiles").upsert(updates);

      if (error) {
        throw error;
      }
      setUpdating("");
      toast.success("Profile updated successfully!");
    } catch (error) {
      setUpdating("");
      handleError(error as Error);
    }
  };

  const importLocations = async (content: FieldValues) => {
    setUpdating("location");
    try {
      const jsonResults = JSON.parse(content.facebook_page_search);
      console.log("PARSED LOCATIONS", jsonResults);

      let { data: location_search, error: error1 } = await supabase
        .from("location_search")
        .select("id");

      if (error1) {
        throw error1;
      }

      const ids = location_search?.map((r) => r.id);

      console.log("IDS", ids);

      const newLocations = jsonResults
        .slice(0, 5)
        .filter((id: number) => !ids?.includes(id))
        .map((l: any) => ({
          location_id: l.id,
          name: l.name,
          details: l.location,
        }));

      console.log("LOCATIONS TO ADD", newLocations);

      let { error: error2 } = await supabase
        .from("location_search")
        .insert(newLocations);

      if (error2) {
        throw error2;
      }

      setUpdating("");
      toast.success("Locations imported successfully!");
    } catch (e) {
      setUpdating("");
      handleError(e as Error);
    }
  };

  const addScheduleEntry = async () => {
    if (!newScheduleEntry.day || !newScheduleEntry.time) {
      toast.error("Please select a day and time before adding.");
      return;
    }
    // Add the new entry to the state
    const newScheduleEntries = [...scheduleEntries, newScheduleEntry];
    await saveSchedule(newScheduleEntries);
    setScheduleEntries(newScheduleEntries);
    // Reset the new entry
    setNewScheduleEntry({ day: "", time: "" });
  };

  const removeScheduleEntry = async (index: number) => {
    const filtered = scheduleEntries.filter((_, i) => i !== index);
    await setScheduleEntries(filtered);
    saveSchedule(filtered);
  };

  const updateScheduleEntry = (field: "day" | "time", value: string) => {
    setNewScheduleEntry({
      ...newScheduleEntry,
      [field]: value,
    });
  };

  const saveSchedule = async (scheduleEntriesList: ScheduleEntry[]) => {
    setUpdating("schedule");
    try {
      const validEntries = scheduleEntriesList.filter(
        (entry) => entry.day && entry.time
      );

      // Save to profiles table
      const updates: any = {
        id: session?.user?.id,
        posting_schedule: validEntries,
        updated_at: new Date().toISOString(),
      };

      let { error } = await supabase.from("profiles").upsert(updates);

      if (error) {
        throw error;
      }

      setUpdating("");
      toast.success("Schedule saved successfully!");
    } catch (e) {
      setUpdating("");
      handleError(e as Error);
    }
  };

  const savePostTemplate = async () => {
    setUpdating("template");
    try {
      const updates = {
        id: session?.user?.id,
        post_template: postTemplate,
        updated_at: new Date().toISOString(),
      };

      let { error } = await supabase.from("profiles").upsert(updates);

      if (error) {
        throw error;
      }

      setUpdating("");
      toast.success("Post template saved successfully!");
    } catch (e) {
      setUpdating("");
      handleError(e as Error);
    }
  };

  return (
    <Layout>
      <div className="bg-base-300">
        <div className="container max-w-5xl mx-auto my-10 px-6">
          <span className="prose">
            <h1 className="mb-8">Profile details</h1>
          </span>

          <div className="card w-full bg-base-100  mb-12 drop-shadow-xl">
            <div className="card-body">
              <form onSubmit={handleSubmit(updateProfile)}>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Name</span>
                  </label>
                  <input
                    placeholder="Name"
                    {...register("username", { required: true })}
                    className="input  w-full"
                  />
                  {errors.username && <span>This field is required</span>}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Email</span>
                  </label>
                  <input
                    placeholder="Email"
                    value={session?.user.email}
                    disabled={true}
                    className="input  w-full"
                  />
                  {errors.email && <span>This field is required</span>}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Website</span>
                  </label>
                  <input
                    placeholder="Website"
                    {...register("website")}
                    className="input  w-full"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Timezone</span>
                  </label>
                  <select
                    {...register("timezone")}
                    className="select select-bordered w-full"
                  >
                    <option value="">Select your timezone</option>
                    {COMMON_TIMEZONES.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                </div>

                <button type="submit" className="btn btn-primary mt-4">
                  {updating === "profile" && (
                    <span className="loading loading-spinner"></span>
                  )}
                  Update Profile
                </button>
              </form>
            </div>
          </div>

          <span className="prose">
            <h1 className="mb-8">Import locations data</h1>
          </span>

          <div className="card w-full bg-base-100  mb-12 drop-shadow-xl">
            <div className="card-body">
              <p>Import a list of Facebook Search Page results.</p>

              <form onSubmit={handleSubmit(importLocations)}>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">JSON Data</span>
                  </label>
                  <textarea
                    {...register("facebook_page_search")}
                    className="input  w-full"
                    rows={15}
                  />
                </div>

                <button type="submit" className="btn btn-primary mt-4">
                  {updating === "location" && (
                    <span className="loading loading-spinner"></span>
                  )}
                  Import
                </button>
              </form>
            </div>
          </div>

          <span className="prose">
            <h1 className="mb-8">Posting Schedule</h1>
          </span>

          <div className="card w-full bg-base-100 mb-12 drop-shadow-xl">
            <div className="card-body">
              <p className="mb-4">
                Set up your posting schedule by selecting days and times when
                you want to post social media content. Times will be interpreted
                based on your selected timezone in the profile settings above.
              </p>

              <div className="space-y-4">
                {scheduleEntries.filter((entry) => entry.day && entry.time)
                  .length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {scheduleEntries
                      .filter((entry) => entry.day && entry.time)
                      .map((entry, index) => (
                        <div
                          key={index}
                          className="badge badge-xl badge-neutral gap-2"
                        >
                          <span>
                            <span className="capitalize">{entry.day}</span> at{" "}
                            {entry.time}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              removeScheduleEntry(
                                scheduleEntries.indexOf(entry)
                              )
                            }
                            className="btn btn-ghost btn-xs text-error"
                            title="Remove this schedule entry"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-gray-500 italic">
                    No schedule entries yet. Add one below.
                  </p>
                )}

                {/* Form for adding new entries */}

                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-2">Add New Schedule Entry:</h4>

                  <div className="flex gap-4 items-center mb-2">
                    <div className="form-control flex-1">
                      <select
                        value={newScheduleEntry.day}
                        onChange={(e) =>
                          updateScheduleEntry("day", e.target.value)
                        }
                        className="select select-bordered w-full"
                      >
                        <option value="">Select day</option>
                        <option value="monday">Monday</option>
                        <option value="tuesday">Tuesday</option>
                        <option value="wednesday">Wednesday</option>
                        <option value="thursday">Thursday</option>
                        <option value="friday">Friday</option>
                        <option value="saturday">Saturday</option>
                        <option value="sunday">Sunday</option>
                      </select>
                    </div>

                    <div className="form-control flex-1">
                      <input
                        type="time"
                        value={newScheduleEntry.time}
                        onChange={(e) =>
                          updateScheduleEntry("time", e.target.value)
                        }
                        className="input input-bordered w-full"
                        placeholder="Select time"
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={addScheduleEntry}
                  className="btn btn-secondary"
                >
                  Add Time Slot
                </button>
              </div>
            </div>
          </div>

          <span className="prose">
            <h1 className="mb-8">Post Template</h1>
          </span>

          <div className="card w-full bg-base-100 mb-12 drop-shadow-xl">
            <div className="card-body">
              <p className="mb-4">
                Set up a default template for your posts. This template will be
                used to format your posts automatically.
              </p>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Post Template</span>
                </label>
                <textarea
                  value={postTemplate}
                  onChange={(e) => setPostTemplate(e.target.value)}
                  className="textarea textarea-bordered w-full h-32"
                  placeholder="Enter your default post template here..."
                  rows={6}
                />
              </div>

              <button
                type="button"
                onClick={savePostTemplate}
                className="btn btn-primary mt-4"
              >
                {updating === "template" && (
                  <span className="loading loading-spinner"></span>
                )}
                Save Template
              </button>
            </div>
          </div>

          <span className="prose">
            <h1 className="mb-8">Notifications</h1>
          </span>

          <div className="card w-full bg-base-100  mb-16 drop-shadow-xl">
            <div className="card-body">
              <div className="onesignal-customlink-container"></div>
            </div>
          </div>

          <span className="prose">
            <h1 className="mb-8">Media Tools</h1>
          </span>

          <GenerateMissingImages />

          <span className="prose">
            <h1 className="mb-8">Sign Out</h1>
          </span>

          <div className="card w-full bg-base-100  mb-16 drop-shadow-xl">
            <div className="card-body">
              <button
                type="button"
                className="btn btn-error"
                onClick={() => supabase.auth.signOut()}
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Account;
